import { AxiosError } from "axios";
// eslint-disable-next-line no-restricted-imports
import i18n from "i18next";
import { useEffect, useMemo } from "react";

import { switchOrg } from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import { FullSectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { ToastKeys } from "i18n/i18next";
import { ListItem } from "mds/components/ListItem";
import { BookSectionIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentUserId,
  selectSectionsThatCanBeAuthored,
  termSelectors,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import {
  toastLocalizedOperationCatcher,
  toastLocalizedOperationError,
  toastSuccessMessage,
} from "utils/alerts";
import { reportApiFailure } from "utils/api-errors";
import { getOrgIdFromCookie } from "utils/auth";
import { useNavigate, useQueryParam } from "utils/routing";

interface SectionsByTerm {
  [termId: string]: FullSectionType[];
}

interface SectionListItemProps {
  section: FullSectionType;
  isLinked: boolean;
  onSelectSection: (sectionId: string) => void;
}

const SectionListItem: React.FC<SectionListItemProps> = ({
  section,
  isLinked,
  onSelectSection,
}) => (
  <ListItem
    className="flex justify-between"
    // @ts-expect-error disabled is not typed
    disabled={isLinked}
    label={section.full_title}
    onClick={isLinked ? undefined : () => onSelectSection(section.id)}
  >
    <div className="flex items-center gap-2">
      <BookSectionIcon />
      <span className={isLinked ? "text-black-tint-55" : ""}>{section.full_title}</span>
      {isLinked && (
        <span className="ml-2 text-black-tint-55">
          {t("link.connected_section", { lmsSectionId: section.lms_section_id })}
        </span>
      )}
    </div>
  </ListItem>
);

export default function Layout() {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const sectionsThatCanBeAuthored = useListSelector(selectSectionsThatCanBeAuthored);
  const unlinkedSectionsThatCanBeAuthored = useMemo(
    () => sectionsThatCanBeAuthored.filter((section) => section.lms_section_id === null),
    [sectionsThatCanBeAuthored],
  );
  const terms = useAppSelector(termSelectors.selectAll);
  const navigate = useNavigate();

  const orgId = useQueryParam("orgId");
  const orgIdFromCookie = getOrgIdFromCookie();

  useEffect(() => {
    const maybeSwitchOrg = async () => {
      if (orgId && orgId !== orgIdFromCookie) {
        try {
          await switchOrg(orgId);
          window.location.reload();
        } catch (error) {
          reportApiFailure("Failed to auto-switch org", error);
        }
      }
    };
    maybeSwitchOrg();
  }, [orgIdFromCookie, orgId]);

  useEffect(() => {
    if (!currentUserId) return;

    storeApi.section_users.list({ user_id: currentUserId });
    storeApi.course_users.list({ user_id: currentUserId });
    storeApi.course_grouping_users.list({ user_id: currentUserId });
    storeApi.courses.list();
    storeApi.terms.list();
  }, [currentUserId]);

  const sectionsByTerm = useMemo(() => {
    return unlinkedSectionsThatCanBeAuthored.reduce((acc: SectionsByTerm, section) => {
      const termId = section.term_id;
      return {
        ...acc,
        [termId]: [...(acc[termId] || []), section],
      };
    }, {} as SectionsByTerm);
  }, [unlinkedSectionsThatCanBeAuthored]);

  const lmsSectionId = useQueryParam("lmsSectionId");

  if (unlinkedSectionsThatCanBeAuthored.length === 0) {
    return (
      <LoadingScreen>
        <div className="flex flex-col gap-4 text-center">
          <div>{t("link.section_not_connected_1")}</div>
          <div>{t("link.section_not_connected_2")}</div>
        </div>
      </LoadingScreen>
    );
  }

  if (!lmsSectionId) {
    return <LoadingScreen>{t("link.no_sections_available")}</LoadingScreen>;
  }

  const onSelectSection = (sectionId: string) => {
    storeApi.sections
      .partial_update(
        {
          id: sectionId,
          lms_section_id: lmsSectionId,
        },
        { skipToast: true },
      )
      .then(() => {
        toastSuccessMessage(t("success.toasts.connect_section_started_success"));
        navigate(`/redirect?sectionId=${sectionId}`);
      })
      .catch((error: AxiosError) => {
        // TODO: make a generic function for this
        const data = error.response?.data as Record<string, string>;

        if (data && "error" in data) {
          const dataError = data.error as ToastKeys;
          if (!i18n.exists(`error.toasts.${dataError}`)) {
            // If it doesn't exist, default to a generic error
            toastLocalizedOperationError("unknown");
          } else {
            toastLocalizedOperationError(dataError);
          }
        } else {
          toastLocalizedOperationCatcher("connect_lms_failure")(error);
        }
      });
  };

  return (
    <div>
      {Object.entries(sectionsByTerm).map(([termId, termSections]) => {
        const term = terms.find((termItem) => termItem.id === termId);
        const termTitle = term ? term.title : "Unassigned";
        return (
          <div key={termId}>
            <h2 className="mb-2 text-lg font-bold">{termTitle}</h2>
            {termSections.map((section) => (
              <SectionListItem
                isLinked={section.lms_section_id !== null}
                key={section.id}
                section={section}
                onSelectSection={onSelectSection}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
